import {useState, useMemo, useEffect} from "react";
import range from "lodash/range";
import {XText, useElementKeyPress} from "@cdx/common";
import Helper from "../Helper";
import {NoneButton} from "./shared";
import {getShownEffort} from "../../features/workflows/workflow-utils";
import {Box, Col, DSInput, Row, css, DSListTile, DSShortcutKey} from "@cdx/ds";
import {dsColors} from "@cdx/ds/css/tokens";

const effortHelper = (
  <Helper
    heading={
      <>
        What is effort? <DSShortcutKey size="lg">e</DSShortcutKey>
      </>
    }
  >
    <p>
      Effort is a dimensionless value that expresses "estimated quantity of work described on this
      card, relative to other cards".
    </p>
    <p>
      If you're a scrum aficionado, think of it as ideal days or story points. You can also use
      effort to enter absolute values if you prefer (e.g. hours).
    </p>
    <p>
      Make sure you populate this field in order to benefit from all the project metrics we offer.
      Note that you can change the effort scale in your project settings.
    </p>
  </Helper>
);

export const EffortPicker = (props) => {
  const {account, card, value, onChange, autoFocus} = props;
  const [customEffort, setCustomEffort] = useState(null);
  const {effortScale} = account;
  const hasChildCards = card && card.childCards && card.childCards.length > 0;
  const getCardId = () => {
    if (!card) return null;
    return card.$meta.modelName === "card"
      ? card.$meta.get("cardId", null)
      : card.$meta.get("itemId", null);
  };
  const cardId = getCardId();

  useEffect(() => {
    return () => {
      setCustomEffort(null);
    };
  }, [cardId]);

  let displayCustomValue = "";
  if (customEffort !== null) {
    displayCustomValue = customEffort;
  } else {
    displayCustomValue = effortScale.includes(value) ? "" : value;
  }

  const keyHandlers = useMemo(
    () =>
      range(10).reduce((m, i) => {
        m[i] = {fn: () => onChange({effort: i})};
        return m;
      }, {}),
    [onChange]
  );
  const handlers = useElementKeyPress(keyHandlers);
  return (
    <Col sp="12px" pt="16px" pb="24px">
      <Row align="center" sp="8px">
        <Box as="h3" size={12} bold color="primary" textTransform="uppercase">
          Effort
        </Box>
        {effortHelper}
        <NoneButton
          onClick={() => onChange({effort: null})}
          active={value === null}
          className={css({ml: "auto"})}
        />
      </Row>
      <Row sp="4px" wrap={effortScale.length > 8}>
        {effortScale.map((val, i) => (
          <DSListTile
            key={val}
            active={value === val && customEffort === null}
            onClick={() => onChange({effort: val})}
            className={css({flex: "auto", maxWidth: "48px"})}
            autoFocus={autoFocus && i === 0}
            {...handlers}
          >
            {val}
          </DSListTile>
        ))}
        <DSInput
          onFocus={() => setCustomEffort(value === null ? "" : value)}
          onBlur={() => {
            setCustomEffort(null);
            if (customEffort !== value)
              onChange({effort: customEffort === "" ? null : customEffort});
          }}
          onChange={(val) => setCustomEffort(parseInt(val, 10) || "")}
          onKeyDown={(e) => {
            if (e.which === 13) onChange({effort: customEffort === "" ? null : customEffort});
          }}
          value={displayCustomValue || ""}
          placeholder={customEffort === null ? "" : "_"}
          size="xs"
          className={css({
            maxWidth: "48px",
            width: "32px",
            flex: "auto",
            textAlign: "center",
            weight: "bold",
          })}
          style={{backgroundColor: dsColors.gray25}}
        />
      </Row>
      {hasChildCards ? (
        <Row align="baseline" justify="center" sp="4px">
          <XText preset="bold" size={0} color="gray700">
            Total effort incl. sub cards:
          </XText>
          <XText preset="bold" size={1}>
            {getShownEffort(card, account)}
          </XText>
        </Row>
      ) : null}
    </Col>
  );
};
